/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
import getOr from 'lodash/fp/getOr.js';
import { change } from 'redux-form';
import { modelTypesById } from '#constants/models/modelTypes/index.js';
import { modelsById } from '#selectors/entities/models.js';
import {
  acceptField,
  rejectField,
  acceptAllFields,
  rejectAllFields,
} from '#actions/models/modelForm.js';
import { getFormSaveFlush } from '#middlewares/support/formSaveCancelRegistry.js';

/*
 * Accept / reject of AI proposals (UI design §3.4). Marking is FORM STATE, and these transitions are
 * local-first: each flips the field's marking (aiStatus/provenance) in the live form IMMEDIATELY -- the
 * field's controls, chip, sidebar pending count, and run-gate all update on the spot, with NO blocking
 * server round-trip. Persistence is the model's ORDINARY save (calculateModelPutPayload / putModel):
 * because marking lives in the redux-form slice it rides that save, so there is NO dedicated accept/
 * reject write path.
 *
 * Each transition is a DISCRETE commit (unlike typing), so rather than leaving the save on the 2s
 * debounce we FLUSH it immediately. That is not just about latency: the model page re-seeds the form
 * from the ENTITY on poll/update-driven re-inits (Router unmount -> destroy(form) -> re-seed), and a
 * poll can re-fetch the model mid-flight. Flushing fires UPDATE_MODEL_REQUEST synchronously, which
 * marks the model "updating" (the poller skips) and invalidates in-flight fetch requests, so a stale
 * poll response cannot revert the just-flipped marking before it round-trips. The optimistic entity
 * marking (models byId reducer) covers the round-trip window itself (data & API design §4.1).
 */

const flushSave = form => {
  const flushPendingSave = getFormSaveFlush(form);
  if (flushPendingSave) {
    flushPendingSave();
  }
};

// Per-field accept (UI design §3.4): aiStatus proposed -> accepted; value and provenance unchanged
// (§3.3 -- provenance survives acceptance). The field becomes readonly with an Edit control.
export const acceptProposedField = (modelId, name) => (dispatch, getState) => {
  const state = getState();
  const model = getOr({}, modelId, modelsById(state));
  const { modelFormName } = modelTypesById[model.type];

  // Read provenance from form state -- index the map by flat field-name key, matching
  // acceptAllProposedFields (a lodash path would mis-parse a name containing '.'/'[').
  const provenanceMap = getOr({}, `form[${modelFormName}].provenance`, state);
  const provenance = provenanceMap[name] || null;

  // Flip marking in form state (instant, local-first) -> schedules the autosave via saveTypes, then
  // flush it immediately (see module comment).
  dispatch(acceptField(modelFormName, name, provenance));
  flushSave(modelFormName);
};

// Per-field reject (UI design §3.1/§3.4): clear the value via redux-form's own `change` (so the form
// re-resolves cascades exactly as any value change does today, §3.5), and clear the marking (aiStatus
// -> none, provenance -> null) in the same update. This is the same transition whether invoked from a
// pending proposal (control labeled "Reject") or from an already-accepted field (labeled "Edit") --
// there is no in-place edit of an AI-marked value; adjusting one means clearing it and typing anew.
export const rejectProposedField = (modelId, name, form) => (dispatch, getState) => {
  const state = getState();
  const model = getOr({}, modelId, modelsById(state));
  const { fields } = modelTypesById[model.type];

  const autofillerFieldNames = new Set(
    fields.filter(field => field.autofiller).map(field => field.name)
  );

  // Clear the value (re-resolves cascades exactly as any value change does, §3.5), flip marking, then
  // flush -- `change` and REJECT_FIELD are both saveTypes, so the single flushed save carries both the
  // cleared value and the cleared marking.
  dispatch(change(form, name, null));
  dispatch(rejectField(form, name, autofillerFieldNames));
  flushSave(form);
};

// Whole-model accept-all (UI design §3.4): every currently-visible pending proposal -> accepted in one
// update, each keeping its own provenance.
export const acceptAllProposedFields = (modelId, names) => (dispatch, getState) => {
  const state = getState();
  const model = getOr({}, modelId, modelsById(state));
  const { modelFormName } = modelTypesById[model.type];

  // Read provenance for all fields from form state.
  const provenanceMap = getOr({}, `form[${modelFormName}].provenance`, state);
  const fieldsWithProvenance = names.map(name => ({
    name,
    provenance: provenanceMap[name] || null,
  }));

  dispatch(acceptAllFields(modelFormName, fieldsWithProvenance));
  flushSave(modelFormName);
};

// Whole-model reject-all (UI design §3.4): clear every currently-visible pending proposal in a single
// update, mirroring accept-all.
export const rejectAllProposedFields = (modelId, names, form) => (dispatch, getState) => {
  const state = getState();
  const model = getOr({}, modelId, modelsById(state));
  const { fields } = modelTypesById[model.type];

  const autofillerFieldNames = new Set(
    fields.filter(field => field.autofiller).map(field => field.name)
  );

  names.forEach(name => dispatch(change(form, name, null)));
  dispatch(rejectAllFields(form, names, autofillerFieldNames));
  flushSave(form);
};
